/**
 * Máy chủ giả của hạn mức token AI theo tháng (ADR-0088, tenant-service): `/api/v1/tenants/{id}/ai-token-quota`
 * và `/api/v1/security/ai-token-quota`.
 *
 * Tính như backend (ADR-0091 mục 1): hạn mức là ghi đè của tenant nếu có, không thì token mỗi tháng của gói tài
 * nguyên (`mock/goi-tai-nguyen.ts`), cộng token cấp thêm đang hiệu lực; `remaining`, `used_percent` (làm tròn
 * xuống, hạn mức 0 thì 100) và `blocked` suy ra từ đó. Tháng tính theo Asia/Ho_Chi_Minh. Màu và nhãn không ở đây
 * (`modules/tenant/constants.ts`).
 *
 * Tình huống mẫu để soát:
 * - FoxAI (`…0001`, Enterprise): có hạn mức riêng 300 triệu, dùng ít;
 * - Minh Long (`…0002`, Dùng thử — tenant của tài khoản mẫu cổng tenant): túi gói đã dùng 526.310 / 1.000.000, túi
 *   nạp thêm 320.000 / 500.000, thứ tự trừ `MUA_THEM_TRUOC`;
 * - Phương Nam (`…0007`, gói tài nguyên tự tạo "Chuyên nghiệp mở rộng" 80 triệu): đã dùng hết, bị chặn;
 * - `?mock=empty`: không giới hạn (gói chưa đặt, không ghi đè).
 */
import type { ApiError, LoiTruong } from '../lib/errors';
import { phanTramDaDung, TOKEN_TOI_DA } from '../modules/tenant/nghiep-vu/han-muc-token';
import type { AiTokenQuota, PlanAiTokenQuota, PlanCode } from '../modules/tenant/types';
import { mayChuCapThemToken } from './cap-them-token';
import { mayChuGoiTaiNguyen } from './goi-tai-nguyen';
import { TI_LE_DAU_RA } from './goi-nap-token';

/** `month_used` chia đầu vào, đầu ra (ADR-0123 mục 2), cùng tỉ lệ với bảng theo model. */
function chiaVaoRa(thang: number): { month_used: number; month_used_input: number; month_used_output: number } {
  const ra = Math.round(thang * TI_LE_DAU_RA);
  return { month_used: thang, month_used_input: thang - ra, month_used_output: ra };
}

const CAC_GOI: PlanCode[] = ['TRIAL', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'];

const ghiDe = new Map<string, number>([['0a7d3c5e-1f2b-4e6a-9c8d-000000000001', 300_000_000]]);

const daDung = new Map<string, number>([
  ['0a7d3c5e-1f2b-4e6a-9c8d-000000000001', 37_452_118],
  ['0a7d3c5e-1f2b-4e6a-9c8d-000000000002', 526_310],
  ['0a7d3c5e-1f2b-4e6a-9c8d-000000000007', 80_000_000],
]);

/** Ngày đầu tháng này và 00:00 ngày đầu tháng sau, giờ Việt Nam (UTC+7, không có giờ mùa hè). */
function thangHienTai(): { month: string; resets_at: string } {
  const vn = new Date(Date.now() + 7 * 3600_000);
  const y = vn.getUTCFullYear();
  const m = vn.getUTCMonth() + 1;
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  const hai = (n: number) => String(n).padStart(2, '0');
  return { month: `${y}-${hai(m)}-01`, resets_at: `${ny}-${hai(nm)}-01T00:00:00+07:00` };
}

function loi(status: number, code: string, message: string, details?: LoiTruong[]): ApiError {
  return { status, code, message, ...(details ? { details } : {}) };
}

function kiemSo(v: unknown, truong: string, choPhepNull: boolean): void {
  if (v === null && choPhepNull) return;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > TOKEN_TOI_DA) {
    throw loi(400, 'INVALID_ARGUMENT', 'Thông tin hạn mức chưa đúng, vui lòng sửa trường được đánh dấu.', [
      { pointer: `#/${truong}`, detail: 'Số token mỗi tháng phải là số nguyên từ 0 đến 1.000.000.000.000.' },
    ]);
  }
}

/**
 * Token AI khả dụng của một tenant thuộc gói nền `goi`, gói tài nguyên `maGoiTN` (`null` là gói mặc định), tính theo
 * túi như tenant-service (ADR-0091 mục 4): túi gói tài nguyên của tháng (`daDung` là phần đã trừ vào túi này) cộng
 * các túi cấp thêm còn hiệu lực, mỗi túi số dư riêng.
 */
export function hanMucTokenCua(tenantId: string, goi: PlanCode, maGoiTN: string | null): AiTokenQuota {
  const plan_limit = mayChuGoiTaiNguyen.tokenMacDinh(goi);
  const resource_package = mayChuGoiTaiNguyen.cuaTenant(maGoiTN, goi);
  const limit_override = ghiDe.get(tenantId) ?? null;
  const base_limit = limit_override ?? resource_package.ai_tokens_month;
  const base_used = daDung.get(tenantId) ?? 0;
  const base_remaining = Math.max(0, base_limit - base_used);
  const grants = mayChuCapThemToken.tuiDangHieuLuc(tenantId);
  const add_on = grants.reduce((t, g) => t + g.tokens, 0);
  const limit_value = base_limit + add_on;
  const used = base_used + grants.reduce((t, g) => t + g.used, 0);
  const remaining = base_remaining + grants.reduce((t, g) => t + g.remaining, 0);
  return {
    tenant_id: tenantId,
    plan_code: goi,
    ...thangHienTai(),
    plan_limit,
    resource_package,
    limit_override,
    base_limit,
    base_used,
    base_remaining,
    add_on,
    grants,
    priority: mayChuCapThemToken.uuTienCua(tenantId),
    limit_value,
    used,
    remaining,
    used_percent: phanTramDaDung(used, limit_value),
    blocked: remaining === 0,
    // Mẫu: phần đã dùng của túi cấp thêm coi như dùng trong tháng này.
    ...chiaVaoRa(used),
  };
}

/** Dòng `AI_TOKENS_MONTH` của `TenantDetail.quota[]`; không giới hạn thì không có dòng. */
export function dongHanMucToken(tenantId: string, goi: PlanCode, maGoiTN: string | null) {
  const q = hanMucTokenCua(tenantId, goi, maGoiTN);
  return q.limit_value === null ? [] : [{ resource: 'AI_TOKENS_MONTH', limit_value: q.limit_value, used: q.used }];
}

/** Như backend khi chưa có dòng nào: gói và tenant đều không giới hạn. */
export function hanMucTokenKhongGioiHan(tenantId: string, goi: PlanCode): AiTokenQuota {
  return {
    tenant_id: tenantId,
    plan_code: goi,
    ...thangHienTai(),
    plan_limit: null,
    resource_package: null,
    limit_override: null,
    base_limit: null,
    base_used: daDung.get(tenantId) ?? 0,
    base_remaining: null,
    add_on: 0,
    grants: [],
    priority: mayChuCapThemToken.uuTienCua(tenantId),
    limit_value: null,
    used: daDung.get(tenantId) ?? 0,
    remaining: null,
    used_percent: null,
    blocked: false,
    ...chiaVaoRa(daDung.get(tenantId) ?? 0),
  };
}

export const mayChuHanMucToken = {
  datGhiDe(tenantId: string, goi: PlanCode, maGoiTN: string | null, limitOverride: unknown): AiTokenQuota {
    kiemSo(limitOverride, 'limit_override', true);
    if (limitOverride === null) ghiDe.delete(tenantId);
    else ghiDe.set(tenantId, limitOverride as number);
    return hanMucTokenCua(tenantId, goi, maGoiTN);
  },

  theoGoi(): { items: PlanAiTokenQuota[] } {
    return {
      items: CAC_GOI.map((plan_code) => ({ plan_code, limit_value: mayChuGoiTaiNguyen.tokenMacDinh(plan_code) })),
    };
  },

  theoGoiRong(): { items: PlanAiTokenQuota[] } {
    return { items: CAC_GOI.map((plan_code) => ({ plan_code, limit_value: null })) };
  },

  datTheoGoi(goi: PlanCode, limitValue: unknown): PlanAiTokenQuota {
    if (!CAC_GOI.includes(goi)) throw loi(404, 'NOT_FOUND', `Không có gói ${goi}.`);
    kiemSo(limitValue, 'limit_value', false);
    // Hạn mức của gói nền là token của gói tài nguyên mặc định (ADR-0091 mục 1): một chỗ lưu, hai đường sửa.
    mayChuGoiTaiNguyen.datTokenMacDinh(goi, limitValue as number);
    return { plan_code: goi, limit_value: mayChuGoiTaiNguyen.tokenMacDinh(goi) };
  },

  /** `month_used` của tenant, cho bảng chia theo model: tổng `fox_tokens` của bảng phải bằng số này (ADR-0123). */
  daDungThang(tenantId: string): number {
    return (daDung.get(tenantId) ?? 0) + mayChuCapThemToken.tuiDangHieuLuc(tenantId).reduce((t, g) => t + g.used, 0);
  },

  /** Cho kịch bản soát: đặt số đã dùng của một tenant. */
  datDaDung(tenantId: string, so: number): void {
    daDung.set(tenantId, so);
  },
};
